import React, { useEffect, useState } from 'react'
import { supabase } from '../../../supabaseClient'

export default function CustomerSummary({ onViewFull }) {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const now = new Date()
        const sevenDaysAgo = new Date(now - 7 * 24 * 60 * 60 * 1000)

        const { data: customers } = await supabase
          .from('customers')
          .select('user_id, account_status, created_at')

        const { data: orders } = await supabase
          .from('orders')
          .select('user_id, order_status')

        const totalCustomers = customers?.length ?? 0

        const newCustomers7d = (customers ?? []).filter(
          c => new Date(c.created_at) >= sevenDaysAgo
        ).length

        const activeUsers = (customers ?? []).filter(
          c => c.account_status === 'active'
        ).length

        const orderCountMap = {}
        ;(orders ?? []).forEach(o => {
          if (o.order_status === 'completed') {
            orderCountMap[o.user_id] = (orderCountMap[o.user_id] || 0) + 1
          }
        })

        const returningBuyers = totalCustomers > 0
          ? Math.round(
              (Object.values(orderCountMap).filter(c => c >= 2).length / totalCustomers) * 100
            )
          : 0

        setStats({ totalCustomers, newCustomers7d, activeUsers, returningBuyers })
      } catch (err) {
        console.error('CustomerSummary fetch error:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-500">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <h3 className="font-semibold text-gray-800 text-sm">Customers</h3>
        </div>
        {onViewFull && (
          <button
            onClick={onViewFull}
            className="text-xs text-[#A0826D] hover:text-[#8B5A3C] font-medium flex items-center gap-1 transition-colors"
          >
            View Full
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="p-5">
        {loading ? (
          <div className="flex gap-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="flex-1 h-16 bg-gray-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-purple-50 rounded-lg p-3">
              <p className="text-xs text-purple-600 font-medium mb-1">Total Customers</p>
              <p className="text-lg font-bold text-purple-700">
                {stats?.totalCustomers?.toLocaleString() ?? 0}
              </p>
            </div>

            <div className="bg-green-50 rounded-lg p-3">
              <p className="text-xs text-green-600 font-medium mb-1">Active Accounts</p>
              <p className="text-lg font-bold text-green-700">
                {stats?.activeUsers?.toLocaleString() ?? 0}
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-xs text-blue-600 font-medium mb-1">New (7 days)</p>
              <p className="text-lg font-bold text-blue-700">
                +{stats?.newCustomers7d ?? 0}
              </p>
            </div>

            <div className="bg-amber-50 rounded-lg p-3">
              <p className="text-xs text-amber-600 font-medium mb-1">Returning Buyers</p>
              <p className="text-lg font-bold text-amber-700">
                {stats?.returningBuyers ?? 0}%
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}